/**
 * Sets a CSS variable for the given component.
 * If the current browser supports ShadowDOM, the variable will be appended to the <style> tag of the shadowRoot.
 * If it does not (IE, Edge < 80, unit/snapshot tests), the variable will be set as inline style on the hostElement.
 *
 * @param hostElement The component in whose context the CSS variable should be set
 * @param name The name of the CSS variable, e.g. "--myvar"
 * @param value The value of the CSS variable to set, e.g. "var(--eon-white)"
 */
export function setCssVariable(hostElement: HTMLElement, name: string, value: string): void {
  // at least one of those is false in IE, legacy Edge and jest tests (snapshots)
  const browserSupportsShadowDom = window.ShadowRoot != null;
  const componentHasShadowRoot = hostElement.shadowRoot != null;
  // shadowed component in modern browser (FF, Chrome in dev build mode):
  const componentHasShadowStyle =
    componentHasShadowRoot &&
    hostElement.shadowRoot.styleSheets != null &&
    hostElement.shadowRoot.styleSheets.length > 0;
  // shadowed component in modern browser (Chrome in prod mode - no idea why this is different):
  const componentHasAdoptedShadowStyle =
    componentHasShadowRoot && (hostElement.shadowRoot as any).adoptedStyleSheets != null;

  if (componentHasAdoptedShadowStyle) {
    // append the CSS variable declaration to the <style> tag of the shadowRoot but using the adoptedStyleSheets API.
    const sheet: any = new CSSStyleSheet();
    sheet.replaceSync(`:host { ${name}: ${value} }`);
    const elemStyleSheets = (hostElement.shadowRoot as any).adoptedStyleSheets;
    (hostElement.shadowRoot as any).adoptedStyleSheets = [...elemStyleSheets, sheet];
  } else if (componentHasShadowStyle) {
    // append the CSS variable declaration to the <style> tag of the shadowRoot
    (hostElement.shadowRoot.styleSheets[0] as any).addRule(`:host`, `${name}: ${value}`);
  } else if (!componentHasShadowRoot || !browserSupportsShadowDom) {
    // add the CSS variable declaration as inline style to the hostElement
    hostElement.style.setProperty(`${name}`, `${value}`);
  } // else: component might be shadowed in a modern browser but the stylesheet isn't ready yet. Happens frequently in FF/Chrome.
}

/**
 * Returns the value of the given CSS variable defined as inline style on the hostElement (IE/Edge/tests) or within its shadowRoot.
 *
 * @param hostElement The component in whose context the CSS variable is set
 * @param name The name of the CSS variable, e.g. "--myvar"
 */
export function getCssVariable(hostElement: HTMLElement, name: string): string {
  const style = window.getComputedStyle(hostElement);
  if (style != null) return style.getPropertyValue(name);
  return null;
}
